/**
 * BatchOrderDialog — edit a saved "morning batch": drag items into the
 * order they should print, tweak copies, then persist the new order to
 * the backend (PATCH /queue-presets/:id). The queue prints in this order.
 */
import { useEffect, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import SortableList from "@/components/workflow/SortableList";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { GripVertical, Minus, Plus, Save } from "lucide-react";
import { toast } from "sonner";

export default function BatchOrderDialog({ preset, siteId, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!preset) { setItems([]); return; }
    setItems((preset.items || []).map((it, i) => ({
      uid: `${it.product_id}-${i}`,
      product_id: it.product_id,
      product_name: it.product_name || "item",
      copies: it.copies || 1,
    })));
  }, [preset]);

  const setCopies = (uid, next) =>
    setItems((xs) => xs.map((x) => (x.uid === uid
      ? { ...x, copies: Math.max(1, Math.min(99, Number(next) || 1)) }
      : x)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/queue-presets/${preset.id}`, {
        name: preset.name,
        items: items.map((x) => ({ product_id: x.product_id, product_name: x.product_name, copies: x.copies })),
      }, withSite(siteId));
      toast.success("Batch order saved");
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't save order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!preset} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="batch-order-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reorder “{preset?.name}”</DialogTitle>
          <DialogDescription>
            Drag the handle to set the print order. Labels print top-to-bottom.
          </DialogDescription>
        </DialogHeader>

        <SortableList
          items={items}
          getId={(x) => x.uid}
          onReorder={setItems}
          className="space-y-2 max-h-[50vh] overflow-y-auto py-1"
          renderItem={(x, dragHandleProps) => (
            <div
              data-testid={`batch-order-item-${x.product_id}`}
              className="flex items-center gap-2 bg-eucalyptus-50 rounded px-2 py-2 text-sm"
            >
              <button
                data-testid={`batch-order-handle-${x.product_id}`}
                {...dragHandleProps}
                className="text-charcoal-soft hover:text-eucalyptus-900 shrink-0"
                aria-label="Drag to reorder"
              >
                <GripVertical className="w-4 h-4" />
              </button>
              <span className="truncate min-w-0 flex-1 font-medium">{x.product_name}</span>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  data-testid={`batch-order-minus-${x.product_id}`}
                  onClick={() => setCopies(x.uid, x.copies - 1)}
                  disabled={x.copies <= 1}
                  className="w-6 h-6 grid place-items-center rounded border border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100 disabled:opacity-30"
                  aria-label="Fewer copies"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className="w-8 text-center font-mono font-semibold tabular-nums">{x.copies}×</span>
                <button
                  data-testid={`batch-order-plus-${x.product_id}`}
                  onClick={() => setCopies(x.uid, x.copies + 1)}
                  disabled={x.copies >= 99}
                  className="w-6 h-6 grid place-items-center rounded border border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100 disabled:opacity-30"
                  aria-label="More copies"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        />

        <DialogFooter>
          <Button data-testid="batch-order-cancel" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="batch-order-save"
            className="bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2"
            disabled={saving || !items.length}
            onClick={save}
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
